import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Modal, Slide, Stack, Typography, Box, InputLabel, Select, MenuItem, CircularProgress, IconButton, FormHelperText } from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import LayerdIcon from "../../../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../../../icons/uiLucideIcons";
import colors from "../../../../../../../utils/colors";
import { GreenSelectForm, GreenSwitch } from "../../../../../../common/Greens";
import { SaveButton, CancelButton } from "../../../../../../common/Button";
import FormHelpTooltip from "../../../../../../common/FormHelpTooltip";
import {
  WafAction,
  SmartParseSensitivity,
  WafType,
  ConfigAction,
} from "../../../../../../../utils/constants";
import useWAFConfig from "../../../../../../../hooks/user/useWAFConfig";
import useSite from "../../../../../../../hooks/user/useSite";
import useGlobalSite from "../../../../../../../hooks/user/useGlobalSite";
import usePermissions from "../../../../../../../hooks/usePermissions";
import { useNotification } from "../../../../../../../contexts/NotificationContext";
import { validateWafSettingsForm } from "../../../../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../../../super/design";
import { filterWafDrawerSites, siteUidOf } from "../../../../../../../utils/wafDrawerSites";

const helperTextSx = { fontSize: 12, color: colors.neutral[600], mx: 0, mt: 0.5 };

const SelectChevron = (props) => <LayerdIcon icon={UI_ICONS.chevronDown} size={20} color="neutral" {...props} />;

const menuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

const WAF_TYPE_CONFIG = {
  signature: {
    title: "Layerd Signature WAF settings",
    subtitle: "Customize how Layerd AI protects this application from threats.",
    helpTooltip: "Configure the Layerd Signature WAF engine: enable/disable, paranoia level, default action, and per-rule-group overrides for known attack signatures.",
    icon: UI_ICONS.signatureWaf,
    iconColor: "#6371F7",
  },
  smartparse: {
    title: "Layerd AI Smart Parse settings",
    subtitle: "Configure advanced parsing-based threat detection.",
    helpTooltip: "Configure the Smart Parse ML engine: enable/disable, sensitivity, default action, and challenge type for AI-driven threat detection on request payloads.",
    icon: UI_ICONS.globalWaf,
    iconColor: "#6371F7",
  },
};

function isUsableWafConfig(cfg) {
  return Boolean(cfg) && typeof cfg === "object" && !Array.isArray(cfg);
}

function applySignatureFromConfig(cfg, setActive, setAction) {
  setActive(!!cfg?.sd_sig_module_active);
  setAction(cfg?.waf_action_sd_sig || WafAction.DETECT);
}

function applySmartParseFromConfig(cfg, setActive, setSensitivity, setAction) {
  setActive(!!cfg?.smart_parse_module_active);
  setSensitivity(cfg?.smart_parse_sensitivity || SmartParseSensitivity.MEDIUM);
  setAction(cfg?.waf_action_smart_parse || WafAction.DETECT);
}

const WAFSettingsDrawer = ({ type, open, handleClose, onSave }) => {
  const wafConfigContext = useWAFConfig();
  const { settingApply } = useSite();
  const { siteOptions, curSiteId, sitesReady } = useGlobalSite();
  const { getPermissionsForSite, PERMISSIONS, isAdmin } = usePermissions();
  const { showSuccess, showError } = useNotification();

  const getWAFConfig = useMemo(
    () => wafConfigContext?.getWAFConfig || (() => Promise.resolve()),
    [wafConfigContext?.getWAFConfig]
  );

  const configWafSetting = useMemo(
    () => wafConfigContext?.configWafSetting || (() => Promise.resolve()),
    [wafConfigContext?.configWafSetting]
  );
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);
  const [drawerSiteUid, setDrawerSiteUid] = useState("");
  const lastFetchedSiteUidRef = useRef(null);
  const fetchGenerationRef = useRef(0);
  const lastLoadedConfigRef = useRef(null);

  const siteUid = drawerSiteUid;

  const [sd_sig_module_active, set_sd_sig_module_active] = useState(false);
  const [waf_action_sd_sig, set_waf_action_sd_sig] = useState(WafAction.DETECT);

  const [smartparse_module_active, set_smartparse_module_active] = useState(false);
  const [smartparse_sensitivity, set_smartparse_sensitivity] = useState(SmartParseSensitivity.MEDIUM);
  const [waf_action_smartparse, set_waf_action_smartparse] = useState(WafAction.DETECT);

  const config = WAF_TYPE_CONFIG[type] || WAF_TYPE_CONFIG.signature;

  const drawerSites = useMemo(() => {
    const admin = isAdmin();
    return filterWafDrawerSites(siteOptions, {
      isAdmin: admin,
      canConfigureSite: (site) =>
        getPermissionsForSite(site).hasPermission(PERMISSIONS.WAF_CONFIGURE),
    });
  }, [siteOptions, isAdmin, getPermissionsForSite, PERMISSIONS]);

  const selectedSite = useMemo(
    () => drawerSites.find((s) => siteUidOf(s) === siteUid) ?? null,
    [drawerSites, siteUid],
  );

  const canConfigureWAF = useMemo(() => {
    if (!selectedSite) return false;
    if (isAdmin()) return true;
    return getPermissionsForSite(selectedSite).hasPermission(PERMISSIONS.WAF_CONFIGURE);
  }, [selectedSite, isAdmin, getPermissionsForSite, PERMISSIONS]);

  const formValidation = useMemo(
    () =>
      validateWafSettingsForm({
        variant: "drawer",
        type,
        selectedSiteId: siteUid,
        waf_action_sd_sig,
        waf_action_smartparse,
        smartparse_sensitivity,
      }),
    [type, siteUid, waf_action_sd_sig, waf_action_smartparse, smartparse_sensitivity],
  );

  const touchField = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const showFieldErr = useCallback(
    (field) => (submitAttempted || touched[field]) && formValidation.errors[field],
    [submitAttempted, touched, formValidation.errors],
  );

  const canSubmit = canConfigureWAF && !loading && Boolean(siteUid);
  const settingsDisabled = saving || loading || !canConfigureWAF || !siteUid;

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setSaving(false);
      setTouched({});
      setSubmitAttempted(false);
      setDrawerSiteUid("");
      lastFetchedSiteUidRef.current = null;
      lastLoadedConfigRef.current = null;
      fetchGenerationRef.current += 1;
    }
  }, [open]);

  useEffect(() => {
    if (!open || drawerSiteUid) return;
    const match = drawerSites.find((s) => s?.site_id === curSiteId);
    if (match) setDrawerSiteUid(siteUidOf(match));
  }, [open, drawerSites, curSiteId, drawerSiteUid]);

  useEffect(() => {
    if (!open || !drawerSiteUid) return;
    const stillThere = drawerSites.some((s) => String(siteUidOf(s)) === String(drawerSiteUid));
    if (stillThere) return;
    setDrawerSiteUid("");
    lastFetchedSiteUidRef.current = null;
    lastLoadedConfigRef.current = null;
    fetchGenerationRef.current += 1;
    setLoading(false);
    set_sd_sig_module_active(false);
    set_waf_action_sd_sig(WafAction.DETECT);
    set_smartparse_module_active(false);
    set_smartparse_sensitivity(SmartParseSensitivity.MEDIUM);
    set_waf_action_smartparse(WafAction.DETECT);
    setBaselineKey((k) => k + 1);
  }, [open, drawerSites, drawerSiteUid]);

  useEffect(() => {
    if (!open || !siteUid) return;

    if (lastFetchedSiteUidRef.current === siteUid) {
      return;
    }

    const fetchGeneration = ++fetchGenerationRef.current;
    setLoading(true);

    getWAFConfig(siteUid, true)
      .then((cfg) => {
        if (fetchGenerationRef.current !== fetchGeneration) return;
        if (!isUsableWafConfig(cfg)) {
          showError("Failed to load WAF configuration");
          lastFetchedSiteUidRef.current = null;
          lastLoadedConfigRef.current = null;
          setLoading(false);
          return;
        }
        lastFetchedSiteUidRef.current = siteUid;
        lastLoadedConfigRef.current = cfg;
        if (type === "signature") {
          applySignatureFromConfig(cfg, set_sd_sig_module_active, set_waf_action_sd_sig);
        } else if (type === "smartparse") {
          applySmartParseFromConfig(
            cfg,
            set_smartparse_module_active,
            set_smartparse_sensitivity,
            set_waf_action_smartparse,
          );
        }
        setBaselineKey((k) => k + 1);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load WAF config:", err);
        if (fetchGenerationRef.current !== fetchGeneration) return;
        lastFetchedSiteUidRef.current = null;
        lastLoadedConfigRef.current = null;
        showError("Failed to load WAF configuration");
        setLoading(false);
      });
  }, [siteUid, open, getWAFConfig, type, showError]);

  const formSnapshot = useMemo(() => {
    if (type === "signature") {
      return { sd_sig_module_active, waf_action_sd_sig };
    }
    if (type === "smartparse") {
      return { smartparse_module_active, smartparse_sensitivity, waf_action_smartparse };
    }
    return {};
  }, [type, sd_sig_module_active, waf_action_sd_sig, smartparse_module_active, smartparse_sensitivity, waf_action_smartparse]);
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, {
    disabled: saving,
  });

  const handleSiteChange = (event) => {
    const nextUid = String(event.target.value ?? "");
    setDrawerSiteUid(nextUid);
    lastFetchedSiteUidRef.current = null;
    lastLoadedConfigRef.current = null;
    setTouched((prev) => ({ ...prev, selectedSiteId: true }));
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    const validation = validateWafSettingsForm({
      variant: "drawer",
      type,
      selectedSiteId: siteUid,
      waf_action_sd_sig,
      waf_action_smartparse,
      smartparse_sensitivity,
    });
    if (!validation.valid) return;

    if (!canConfigureWAF) {
      showError("You don't have permission to configure WAF for this site");
      return;
    }

    const siteExists = drawerSites.some((site) => String(siteUidOf(site)) === String(siteUid));
    if (!siteExists) {
      console.error("Selected site does not exist in site list");
      return;
    }

    setSaving(true);
    try {
      const baseline = lastLoadedConfigRef.current;
      const tasks = [];
      if (type === "signature") {
        if (baseline?.sd_sig_module_active !== sd_sig_module_active) {
          tasks.push(() => configWafSetting(siteUid, "sdSigWafActiveChange", { enable: sd_sig_module_active }));
        }
        if (baseline?.waf_action_sd_sig !== waf_action_sd_sig) {
          tasks.push(() =>
            configWafSetting(siteUid, "sdSigWafActionChange", {
              cate_id: WafType.SENSEDEFENCE_SIGNATURE,
              action: waf_action_sd_sig,
            })
          );
        }
      } else if (type === "smartparse") {
        if (baseline?.smart_parse_module_active !== smartparse_module_active) {
          tasks.push(() => configWafSetting(siteUid, "smartParseWafActiveChange", { enable: smartparse_module_active }));
        }
        if (baseline?.smart_parse_sensitivity !== smartparse_sensitivity) {
          tasks.push(() => configWafSetting(siteUid, "smartParseSensitivityChange", { sensitivity: smartparse_sensitivity }));
        }
        if (baseline?.waf_action_smart_parse !== waf_action_smartparse) {
          tasks.push(() => configWafSetting(siteUid, "smartParseWafActionChange", { cate_id: WafType.SMART_PARSE, action: waf_action_smartparse }));
        }
      }
      if (tasks.length > 0) {
        for (const task of tasks) {
          await task();
        }
        await getWAFConfig(siteUid);
        if (selectedSite?.site_id && settingApply) {
          await settingApply(selectedSite.site_id, ConfigAction.WAF);
        }
        showSuccess("WAF settings saved successfully");
      }
      if (onSave) {
        onSave();
      }
      handleClose();
    } catch (err) {
      console.error(err);
      showError("Failed to save WAF settings");
    }
    setSaving(false);
  };

  const headerIcon = config?.icon || UI_ICONS.signatureWaf;

  if (!open || !type || !WAF_TYPE_CONFIG[type]) {
    return null;
  }

  const renderSiteSelect = () => {
    const emptyList = drawerSites.length === 0;
    return (
      <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("selectedSiteId"))}>
        <InputLabel id="waf-drawer-site" shrink>
          Site
        </InputLabel>
        <Select
          labelId="waf-drawer-site"
          label="Site"
          IconComponent={SelectChevron}
          displayEmpty
          disabled={saving || emptyList || !sitesReady}
          value={siteUid}
          onClose={() => touchField("selectedSiteId")}
          onChange={handleSiteChange}
          renderValue={(selected) => {
            if (!selected) {
              let placeholder = "Select a site";
              if (!sitesReady) placeholder = "Loading sites...";
              else if (emptyList) placeholder = "No sites you can configure";
              return (
                <Typography sx={{ fontSize: 14, color: colors.neutral[400] }}>
                  {placeholder}
                </Typography>
              );
            }
            const match = drawerSites.find((s) => siteUidOf(s) === selected);
            return (
              <Typography
                sx={{
                  fontSize: 14,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {match?.site_id || selected}
              </Typography>
            );
          }}
        >
          <MenuItem value="" disabled sx={menuItemSx}>
            <Typography sx={{ fontSize: 14, color: colors.neutral[400] }}>
              Select a site
            </Typography>
          </MenuItem>
          {drawerSites.map((site) => {
            const uid = siteUidOf(site);
            return (
              <MenuItem value={uid} key={uid} sx={menuItemSx}>
                {site.site_id}
              </MenuItem>
            );
          })}
        </Select>
        {!sitesReady && (
          <FormHelperText sx={helperTextSx}>Loading sites...</FormHelperText>
        )}
        {sitesReady && emptyList && (
          <FormHelperText sx={helperTextSx}>No sites you can configure</FormHelperText>
        )}
        {!emptyList && showFieldErr("selectedSiteId") && (
          <FormHelperText error sx={helperTextSx}>{formValidation.errors.selectedSiteId}</FormHelperText>
        )}
      </GreenSelectForm>
    );
  };

  const renderSignatureSettings = () => (
    <>
      <Stack
        direction={"row"}
        alignItems={"center"}
        spacing={3}
        justifyContent={"space-between"}
        sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[300]}`, backgroundColor: "white", p: 3 }}
      >
        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
          Layerd Signature WAF
        </Typography>
        <GreenSwitch disabled={settingsDisabled} checked={sd_sig_module_active} onChange={(e) => set_sd_sig_module_active(e.target.checked)} />
      </Stack>
      <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("waf_action_sd_sig"))}>
        <InputLabel id="waf_action_sd">WAF Action</InputLabel>
        <Select
          labelId="waf_action_sd"
          IconComponent={SelectChevron}
          disabled={settingsDisabled}
          value={waf_action_sd_sig}
          onClose={() => touchField("waf_action_sd_sig")}
          onChange={(e) => set_waf_action_sd_sig(parseInt(e.target.value))}
        >
          <MenuItem value={WafAction.DETECT} key={`SDWafAction${WafAction.DETECT}`} sx={menuItemSx}>
            Detect
          </MenuItem>
          <MenuItem value={WafAction.BLOCK} key={`SDWafAction${WafAction.BLOCK}`} sx={menuItemSx}>
            Block
          </MenuItem>
          <MenuItem value={WafAction.CHALLENGE} key={`SDWafAction${WafAction.CHALLENGE}`} sx={menuItemSx}>
            Challenge
          </MenuItem>
        </Select>
        {showFieldErr("waf_action_sd_sig") && (
          <FormHelperText error sx={helperTextSx}>{formValidation.errors.waf_action_sd_sig}</FormHelperText>
        )}
      </GreenSelectForm>
    </>
  );

  const renderSmartParseSettings = () => (
    <>
      <Stack
        direction={"row"}
        alignItems={"center"}
        spacing={3}
        justifyContent={"space-between"}
        sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[300]}`, backgroundColor: "white", p: 3 }}
      >
        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
          Layerd AI Smart Parse
        </Typography>
        <GreenSwitch disabled={settingsDisabled} checked={smartparse_module_active} onChange={(e) => set_smartparse_module_active(e.target.checked)} />
      </Stack>
      <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("smartparse_sensitivity"))}>
        <InputLabel id="smartparse_sensitivity">SmartParse Sensitivity</InputLabel>
        <Select
          labelId="smartparse_sensitivity"
          IconComponent={SelectChevron}
          disabled={settingsDisabled}
          value={smartparse_sensitivity}
          onClose={() => touchField("smartparse_sensitivity")}
          onChange={(e) => {
            set_smartparse_sensitivity(parseInt(e.target.value));
          }}
        >
          <MenuItem value={SmartParseSensitivity.HIGH} key={`SmartParseSensitivity${SmartParseSensitivity.HIGH}`} sx={menuItemSx}>
            High
          </MenuItem>
          <MenuItem value={SmartParseSensitivity.MEDIUM} key={`SmartParseSensitivity${SmartParseSensitivity.MEDIUM}`} sx={menuItemSx}>
            Medium
          </MenuItem>
          <MenuItem value={SmartParseSensitivity.LOW} key={`SmartParseSensitivity${SmartParseSensitivity.LOW}`} sx={menuItemSx}>
            Low
          </MenuItem>
        </Select>
        {showFieldErr("smartparse_sensitivity") && (
          <FormHelperText error sx={helperTextSx}>{formValidation.errors.smartparse_sensitivity}</FormHelperText>
        )}
      </GreenSelectForm>
      <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("waf_action_smartparse"))}>
        <InputLabel id="waf_action_smartparse">WAF Action</InputLabel>
        <Select
          labelId="waf_action_smartparse"
          IconComponent={SelectChevron}
          disabled={settingsDisabled}
          value={waf_action_smartparse}
          onClose={() => touchField("waf_action_smartparse")}
          onChange={(e) => {
            set_waf_action_smartparse(parseInt(e.target.value));
          }}
        >
          <MenuItem value={WafAction.DETECT} key={`SmartParseWafAction${WafAction.DETECT}`} sx={menuItemSx}>
            Detect
          </MenuItem>
          <MenuItem value={WafAction.BLOCK} key={`SmartParseWafAction${WafAction.BLOCK}`} sx={menuItemSx}>
            Block
          </MenuItem>
          <MenuItem value={WafAction.CHALLENGE} key={`SmartParseWafAction${WafAction.CHALLENGE}`} sx={menuItemSx}>
            Challenge
          </MenuItem>
        </Select>
        {showFieldErr("waf_action_smartparse") && (
          <FormHelperText error sx={helperTextSx}>{formValidation.errors.waf_action_smartparse}</FormHelperText>
        )}
      </GreenSelectForm>
    </>
  );

  return (
    <>
    <Modal
      open={open}
      onClose={requestClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}
        >
          <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LayerdIcon icon={headerIcon} size={32} color="primary" />
              </Box>
              <Stack>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                  <Typography
                    sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                  >
                    {config?.title || "WAF Settings"}
                  </Typography>
                  <FormHelpTooltip tooltip={config?.helpTooltip || "Configure WAF engine settings for the selected site."} />
                </Stack>
                <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  {config?.subtitle || "Configure WAF settings for your site."}
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
              <LayerdIcon icon={UI_ICONS.close} size={20} color="neutral" />
            </IconButton>
          </Stack>
          <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 130px)` }}>
            <ReactPerfectScrollbar>
              <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                <Stack spacing={4}>
                  {renderSiteSelect()}
                  {loading && siteUid ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography sx={ { fontSize: 14, color: colors.neutral[600], mt: 2 }}>
                        Loading WAF configuration...
                      </Typography>
                    </Stack>
                  ) : (
                    <>
                      {type === "signature" && renderSignatureSettings()}
                      {type === "smartparse" && renderSmartParseSettings()}
                      <Stack
                        direction={"row"}
                        spacing={2}
                        sx={{
                          minHeight: 48,
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: colors.primary["alpha"],
                          border: `solid 1px ${colors.primary[300]}`,
                        }}
                      >
                        <Box sx={{ width: 24, height: 24 }}>
                          <LayerdIcon icon={UI_ICONS.info} size={24} color="primaryLight" />
                        </Box>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                          {!siteUid
                            ? "Select a site to configure WAF settings. Changes will be applied instantly after save!"
                            : canConfigureWAF
                            ? "You can update, review, or manage settings on this screen. Changes will be applied instantly after save!"
                            : "You don't have permission to configure WAF for this site. Please contact your administrator to request access."
                          }
                        </Typography>
                      </Stack>
                    </>
                  )}
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={3}>
                  <SaveButton
                    onClick={handleSave}
                    disabled={saving || !canSubmit}
                    title={siteUid && !canConfigureWAF ? "You don't have permission to configure WAF for this site" : ""}
                  />
                  <CancelButton onClick={requestClose} />
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
};

export default WAFSettingsDrawer;
